import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * Stat tile, the small metric card per the design system. Mono caps
 * label, big serif value, optional unit suffix, optional delta or
 * spark below. Used in Plan stat row and any place "one number, one
 * caption" is the right shape.
 */
export interface StatTileProps {
  label: string;
  value: ReactNode;
  unitSuffix?: ReactNode;
  /** Caption under the value. Italic, gray-600. */
  caption?: ReactNode;
  /** Optional below-the-fold content (sparkline, dot row, etc.). */
  children?: ReactNode;
  /** When set, the tile takes on the teal-50 highlight treatment. */
  highlight?: boolean;
  className?: string;
}

export function StatTile({
  label,
  value,
  unitSuffix,
  caption,
  children,
  highlight = false,
  className,
}: StatTileProps) {
  return (
    <section
      className={cn(
        "flex flex-col gap-1.5 rounded-xl",
        highlight
          ? "border-2 border-teal-600 bg-teal-50 p-[13px]"
          : "border border-mist bg-white p-3.5",
        className,
      )}
    >
      <div
        className={cn(
          "font-mono text-2xs font-semibold uppercase tracking-[0.16em]",
          highlight ? "text-teal-700" : "text-gray-500",
        )}
      >
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <strong className="font-sans text-3xl font-medium leading-none text-navy [font-variant-numeric:tabular-nums]">
          {value}
        </strong>
        {/* A space between value and unit, so "12 days" is read as two words. Flex layout ignores it. */}
        {unitSuffix ? (
          <>
            {" "}
            <span className="text-xs text-gray-500">{unitSuffix}</span>
          </>
        ) : null}
      </div>
      {children}
      {caption ? <p className="m-0 text-xs leading-snug text-gray-600">{caption}</p> : null}
    </section>
  );
}
